import React, { Fragment, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import classNames from 'classnames';
import dateFormat from 'dateformat';

import styles from '@components/Order/Details/DetailsTable.module.css';

import { BootstrapTable } from '@components/Table/Table';

const renderNumberCell = ({ row, value }) => {
  const { backorder } = row.original;
  if (backorder) {
    return 'Backorder';
  }
  return value;
};

const renderMissingCell = ({ value }) =>
  value || <span className="textRed">Missing</span>;

export const DidOrdersTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
      },
      {
        id: 'backorder',
        Header: '',
        accessor: 'backorder',
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        Cell: renderMissingCell,
        className: 'col-md-1',
      },
      {
        Header: 'Country',
        accessor: 'country',
        className: 'col-md-2',
      },
      {
        id: 'didType',
        Header: 'Type',
        accessor: 'type',
        className: 'col-md-1',
      },
      {
        Header: 'DID/Number',
        accessor: 'did',
        className: 'col-md-1',
        Cell: renderNumberCell,
      },
      {
        Header: 'End user',
        accessor: 'profile',
        Cell: renderMissingCell,
        className: 'col-md-1',
      },
      {
        Header: 'Customer Trunk',
        accessor: 'customer_trunk',
        Cell: renderMissingCell,
        className: 'col-md-1',
      },
      {
        Header: 'TOS',
        accessor: 'tos',
        className: 'col-md-1',
      },
      { Header: 'Amount', accessor: 'amount', className: 'col-md-1' },
      {
        Header: 'Status',
        accessor: 'status',
        className: 'col-md-1',
      },
      {
        Header: 'Cancellation date',
        accessor: 'date_cancel',
        className: 'col-md-1',
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }
          const date = new Date(value);
          return dateFormat(date, 'defaultDate');
        },
      },
    ],
    []
  );
  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize mt-2',
      })}
      hiddenColumns={['id', 'backorder']}
      paginated={data.length > 20}
    />
  );
};

export const DedicatedChannelOrdersTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
      },
      {
        id: 'backorder',
        Header: '',
        accessor: 'backorder',
      },
      { Header: 'Plan', accessor: 'plan.plan', className: 'col-md-1' },
      {
        Header: 'Country',
        accessor: 'country.description',
        className: 'col-md-2',
      },
      {
        id: 'didType',
        Header: 'Type',
        accessor: 'type.description',
        className: 'col-md-1',
      },
      {
        Header: 'DID/Number',
        accessor: 'did',
        className: 'col-md-1',
        Cell: renderNumberCell,
      },
      {
        Header: 'TOS',
        accessor: 'typeOfService.description',
        className: 'col-md-1',
      },
      { Header: 'Amount', accessor: 'amount', className: 'col-md-1' },
      { Header: 'NRC', accessor: 'price.nrc', className: 'col-md-1' },
      { Header: 'MRC', accessor: 'price.mrc', className: 'col-md-1' },
      {
        Header: 'Cancellation date',
        accessor: 'date_cancel',
        className: 'col-md-1',
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }
          const date = new Date(value);
          return dateFormat(date, 'defaultDate');
        },
      },
    ],
    []
  );

  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize mt-2',
      })}
      hiddenColumns={['id', 'backorder']}
      paginated={data.length > 20}
    />
  );
};

export const SharedChannelOrdersTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: '',
        accessor: 'id',
      },
      {
        Header: 'DID-Group',
        accessor: 'did_group',
        className: 'col-md-2',
      },
      { Header: 'Plan', accessor: 'plan', className: 'col-md-1' },
      {
        id: 'type',
        Header: 'Type',
        accessor: 'type',
        className: 'col-md-1',
      },
      {
        Header: 'Additional channels',
        accessor: 'amount',
        className: 'col-md-1',
      },
      { Header: 'NRC', accessor: 'nrc', className: 'col-md-1' },
      { Header: 'MRC', accessor: 'mrc', className: 'col-md-1' },
    ],
    []
  );

  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize mt-2',
      })}
      hiddenColumns={['id', 'backorder']}
      paginated={data.length > 20}
    />
  );
};

const renderActionCell = ({ row, value }) => {
  const { country_id: country, plan_id: plan, plan_type: type } = row.original;

  const props = {
    as: Link,
    to: `${value}?country=${country}&plan=${plan}&type=${type}`,
    variant: 'didxl',
    size: 'sm',
  };
  if (!value) {
    delete props.as;
    delete props.to;
    props.disabled = true;
  }

  if (!country || !plan || !type) {
    props.to = value;
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Button {...props}>See end user</Button>;
};

const renderCommentCell = ({ value }) => {
  if (!value || value.length === 0) {
    return null;
  }
  return value.map(({ id, description }) => (
    <Fragment key={id}>
      <span className={styles.profileComment}>{description}</span>
      <br />
    </Fragment>
  ));
};

const renderStatusCell = ({ row, value }) => {
  const turnTextRed =
    row.original.status.id === 2 || row.original.status.id === 3;
  return <span className={classNames({ textRed: turnTextRed })}>{value}</span>;
};

const renderProfileNameCell = ({ row, value }) => (
  <span className={classNames({ textRed: !row.original.id })}>{value}</span>
);

export const UsedProfilesOrderTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        Header: 'End user',
        accessor: 'name',
        className: 'col-md-1',
        Cell: renderProfileNameCell,
      },
      {
        Header: 'Status',
        accessor: 'status',
        className: 'col-md-1',
        Cell: renderStatusCell,
      },
      {
        Header: 'Comment',
        accessor: 'documents',
        className: 'col-md-1',
        Cell: renderCommentCell,
      },
      {
        Header: 'Action',
        accessor: 'location',
        className: 'col-md-1',
        Cell: renderActionCell,
      },
    ],
    []
  );

  return (
    <BootstrapTable
      columns={columns}
      data={data}
      tableProps={() => ({
        responsive: true,
        bordered: true,
        className: 'normalFontSize mt-2',
      })}
      paginated={data.length > 20}
    />
  );
};
